// ══════════════════════════════════════════════════════════════════════════
//  Capturas por subir en un equipo compartido (2026-09-16)
//
//  Cada registro de la cola offline guarda quién capturó (perfilId) y la cola solo
//  sube lo de su dueño vigente (offlineQueue.js). Este motor decide qué ve y qué se
//  le pregunta a la persona con sesión:
//   - el chip de la barra superior (OfflineStatus.js) cuenta como suyas solo las
//     capturas que sube su sesión; las de otra cuenta se cuentan aparte y solo su
//     dueño las reintenta o descarta;
//   - al cerrar sesión (App.js), si deja capturas suyas sin subir, se le dice que se
//     quedan en el equipo y que suben cuando vuelva a entrar.
//
//  Suya = la capturó esa persona, o no tiene dueño: los registros anteriores a esta
//  fecha suben con quien esté, como siempre. Es ESPEJO EXACTO de puedeSubir en
//  offlineQueue.js, que es quien de verdad sube: si cambia una regla, cambia la otra.
// ══════════════════════════════════════════════════════════════════════════

const entero = (v) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);
const capturas = (n) => `${n} captura${n === 1 ? '' : 's'}`;

/** true si la sesión de `perfilId` sube (y decide sobre) este registro de la cola. */
export function esDeLaSesion(registro, perfilId) {
  if (!registro) return false;
  return !registro.perfilId || registro.perfilId === (perfilId || null);
}

/**
 * Los conteos de outbox.getStatus() repartidos por dueño. ajenos y errorAjenos son
 * las pendientes y las fallidas de otra persona, que siguen contando en pending y en
 * error; se acotan a esos totales para que un recuento a medias nunca dé negativos.
 * @returns {{ porSubir: number, sinPoderSubir: number, deOtraCuenta: number }}
 */
export function resumenCapturas(estado) {
  const e = estado || {};
  const pendientes = entero(e.pending);
  const fallidas = entero(e.error);
  const pendientesAjenas = Math.min(entero(e.ajenos), pendientes);
  const fallidasAjenas = Math.min(entero(e.errorAjenos), fallidas);
  return {
    porSubir: pendientes - pendientesAjenas,
    sinPoderSubir: fallidas - fallidasAjenas,
    deOtraCuenta: pendientesAjenas + fallidasAjenas,
  };
}

/**
 * Los registros de la cola (outbox.getPending y getFailed) repartidos por dueño, para
 * las listas del panel. De otra cuenta solo se da el número: no se listan ni se tocan.
 * @returns {{ porSubir: object[], sinPoderSubir: object[], deOtraCuenta: number }}
 */
export function repartirCapturas(registros, perfilId = null) {
  const pendientes = (registros && registros.pendientes) || [];
  const fallidas = (registros && registros.fallidas) || [];
  const suya = (r) => esDeLaSesion(r, perfilId);
  const porSubir = pendientes.filter(suya);
  const sinPoderSubir = fallidas.filter(suya);
  return {
    porSubir,
    sinPoderSubir,
    deOtraCuenta: (pendientes.length - porSubir.length) + (fallidas.length - sinPoderSubir.length),
  };
}

/** «N capturas de otra cuenta esperan a su dueño», o null si no hay. */
export function textoDeOtraCuenta(n) {
  const k = entero(n);
  if (!k) return null;
  return k === 1
    ? '1 captura de otra cuenta espera a su dueño'
    : `${k} capturas de otra cuenta esperan a su dueño`;
}

/**
 * Cómo se ve el chip. El estado normal (con red y sin nada de nadie) no se anuncia.
 * Prioridad: lo suyo que no pudo subir (pide decisión) > lo suyo por subir (se resuelve
 * solo) > sin red > capturas de otra cuenta. La cifra es solo de lo suyo: en tono
 * neutro no lleva número, para que no se lea como propio.
 * @returns {{ visible: boolean, tono: 'critico'|'aviso'|'neutro', numero: number, titulo: string }}
 */
export function estadoChip(resumen, online = true) {
  const r = resumen || {};
  const sinPoderSubir = entero(r.sinPoderSubir);
  const porSubir = entero(r.porSubir);
  const deOtraCuenta = entero(r.deOtraCuenta);
  if (sinPoderSubir > 0) {
    return { visible: true, tono: 'critico', numero: sinPoderSubir, titulo: `${capturas(sinPoderSubir)} sin poder subir` };
  }
  if (porSubir > 0) {
    return { visible: true, tono: 'aviso', numero: porSubir, titulo: `${capturas(porSubir)} por subir` };
  }
  if (!online) {
    return { visible: true, tono: 'neutro', numero: 0, titulo: 'Sin conexión: la captura se guarda en el equipo' };
  }
  if (deOtraCuenta > 0) {
    return { visible: true, tono: 'neutro', numero: 0, titulo: textoDeOtraCuenta(deOtraCuenta) };
  }
  return { visible: false, tono: 'neutro', numero: 0, titulo: '' };
}

/**
 * Capturas de la persona con sesión que se quedan en el equipo si sale ahora: las
 * pendientes que sube su sesión. Sin dueño en la cola no hay «tuyas». Las que no
 * pudieron subir no cuentan aquí: no suben solas al volver, esperan su decisión.
 */
export function tuyasPorSubir(estado) {
  if (!estado || !estado.dueno) return 0;
  return resumenCapturas(estado).porSubir;
}

/** El mensaje de la confirmación al cerrar sesión, o null si no hay que preguntar. */
export function textoSalida(n) {
  const k = entero(n);
  if (!k) return null;
  return k === 1
    ? 'Hay 1 captura tuya sin subir. Se queda en este equipo y se subirá cuando vuelvas a entrar.'
    : `Hay ${k} capturas tuyas sin subir. Se quedan en este equipo y se subirán cuando vuelvas a entrar.`;
}
